<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { onDestroy, untrack } from 'svelte';
    import { __ } from '@oat-sa-private/ui-core';
    import settingsKeys from '../../../settings/settingsKeys.js';
    import Stepper from './generic/Stepper.svelte';

    const origFontSizePercent = 50; /* value comes from @oat-sa-private/ui-identity _typography.css */
    const defaultValue = 0;

    /**
     * @typedef {Object} Props
     * @property {Object} [config]
     * @property {Array} [config.zoomLevels]
     * @property {Object} initialState
     * @property {Function} onChange
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        config = { zoomLevels: [] },
        initialState,
        onChange
    } = $props();

    // stepper props
    const min = 0;
    const step = 1;
    let max = $state(5);
    let value = $state(defaultValue);
    const ariaLabelDecr = __('zoom out');
    const ariaLabelIncr = __('zoom in');

    const allZoomLevels = [100, 110, 125, 150, 175, 200];
    const zoomLevels = allZoomLevels.filter(lvl => config?.zoomLevels?.includes(lvl));
    max = zoomLevels.length - 1;

    let zoomLevel = $derived(zoomLevels[value]);

    const initialStateOnce = untrack(() => initialState);
    if (initialStateOnce?.value) {
        handleZoomChange(initialStateOnce);
    }

    /**
     * Page zoom implementation: change base rem size
     * - consistent rendering across all components
     * - no issue with measured elements
     * - no issue with drag&drop
     * - it only works if the change is made on the documentElement (<html>) level
     * @param {Object} [param1]
     * @param {Number} [param1.value]
     * @param {Boolean} [param1.nonDefault]
     */
    function handleZoomChange({ value: newValue, nonDefault }) {
        if (typeof newValue === 'number' && newValue >= min && newValue <= max) {
            value = newValue;
            zoomLevel = zoomLevels[value] || zoomLevels[0];
            document.documentElement.style.fontSize = `${(origFontSizePercent * zoomLevel) / 100}%`;
            document.body.dataset.zoomLevel = `${zoomLevel}`;

            onChange?.({
                key: settingsKeys.pageZoom,
                state: {
                    value,
                    zoomLevel,
                    nonDefault
                }
            });
        }
    }

    /**
     * Unset DOM attributes on destroy
     */
    onDestroy(() => {
        document.documentElement.style.fontSize = `${origFontSizePercent}%`;
        delete document.body.dataset.zoomLevel;
    });
</script>

<Stepper {min} {max} {step} {value} {ariaLabelDecr} {ariaLabelIncr} onChange={handleZoomChange} />
